import React from "react"
import Hls from "hls.js"
import toast from "react-hot-toast"
import { useApp } from "../context/AppContext"
import { Icon, useIsMobile } from "../shared"
import { useOrgName } from "../shared/BrandLogo"
import { Card, Badge, Progress, Btn } from "../shared/components"
import { listCourses, getCourse, setLessonProgress, getModuleDocDownloadUrl, getCourseDocDownloadUrl } from "../api/courses.api"

// pdf.js berukuran ratusan KB dan hanya dibutuhkan saat sebuah dokumen benar-
// benar dibuka. Diimpor statis, ia ikut ke chunk utama untuk setiap peserta —
// termasuk yang seluruh materinya video.
const PDFReaderModal = React.lazy(() =>
  import("./PDFReaderModal").then((m) => ({ default: m.PDFReaderModal })))
const PDFContinuousReader = React.lazy(() => import("./PDFContinuousReader"))
import {
  getVideo,
  updateWatchProgress,
  listBookmarks,
  addBookmark,
  deleteBookmark,
} from "../api/videos.api"
import { tintBg, tintFg } from '../lib/tint'
import { modLabel, modHeading, withLabel } from '../lib/modLabel'

function formatTime(sec) {
  if (!Number.isFinite(sec) || sec < 0) return "0:00"
  const h = Math.floor(sec / 3600)
  const m = Math.floor((sec % 3600) / 60)
  const s = Math.floor(sec % 60)
  if (h > 0) return `${h}:${m.toString().padStart(2, "0")}:${s.toString().padStart(2, "0")}`
  return `${m}:${s.toString().padStart(2, "0")}`
}

/* ============================================================
   Learning screens: Course detail (two-pane viewer),
   reusable Video player, standalone routes.
   ============================================================ */

// Per-lesson-type visual config. Backend LessonType enum = VIDEO | PDF | GAME.
const LESSON_TYPES = {
  VIDEO: { icon: "play", color: "var(--program-color)", soft: "var(--program-color-soft)" },
  PDF:   { icon: "doc",  color: tintFg("#B53A2C"), soft: tintBg("#B53A2C") },
  GAME:  { icon: "compass", color: tintFg("#2E7D5B"), soft: tintBg("#2E7D5B") },
}
function lessonTypeLabel(type, lang) {
  if (type === "VIDEO") return "Video"
  if (type === "PDF") return lang === "id" ? "Dokumen" : "Document"
  if (type === "GAME") return lang === "id" ? "Simulasi" : "Simulation"
  return type
}

// =============================================================
// REUSABLE: Video player (real HLS + progress + bookmarks)
// Used by both the in-course LessonFullPage and the standalone
// VideoPlayerScreen route. `video` is an already-loaded Video row
// (getCourse includes it; the standalone route fetches it).
// =============================================================
function LessonVideoPlayer({ video, lessonId, programId, user, currentProgram, lang, onCompleted }) {
  const videoRef = React.useRef(null)
  const hlsRef = React.useRef(null)
  const maxPctRef = React.useRef(0)

  const [playing, setPlaying] = React.useState(false)
  const [currentTime, setCurrentTime] = React.useState(0)
  const [duration, setDuration] = React.useState(0)
  const [playbackRate, setPlaybackRate] = React.useState(1)
  const [bookmarks, setBookmarks] = React.useState([])
  const [savingBookmark, setSavingBookmark] = React.useState(false)
  const [completed, setCompleted] = React.useState(false)

  // Load bookmarks for this video
  React.useEffect(() => {
    let cancelled = false
    if (programId && video?.id) {
      listBookmarks(programId, video.id)
        .then((bs) => !cancelled && setBookmarks(bs))
        .catch(() => {})
    }
    return () => { cancelled = true }
  }, [programId, video?.id])

  // Attach HLS source when video metadata + ref are ready
  React.useEffect(() => {
    const el = videoRef.current
    const src = video?.hls_url
    if (!el || !src) return
    const isHls = /\.m3u8(\?|$)/i.test(src)
    if (isHls && Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true })
      hls.loadSource(src)
      hls.attachMedia(el)
      hlsRef.current = hls
    } else {
      el.src = src
    }
    return () => {
      if (hlsRef.current) {
        try { hlsRef.current.destroy() } catch {}
        hlsRef.current = null
      }
    }
  }, [video?.hls_url])

  // Send watch progress to backend periodically while playing
  React.useEffect(() => {
    if (!playing || !video || !programId) return
    const interval = setInterval(() => {
      const el = videoRef.current
      if (!el || !el.duration) return
      const pct = Math.min(100, (el.currentTime / el.duration) * 100)
      if (pct > maxPctRef.current) maxPctRef.current = pct
      const isDone = maxPctRef.current >= 90
      updateWatchProgress(programId, video.id, {
        watchPct: Math.round(maxPctRef.current),
        completed: isDone,
      }).catch(() => {})
      if (isDone && !completed) { setCompleted(true); onCompleted?.() }
    }, 10000)
    return () => clearInterval(interval)
  }, [playing, video, programId, completed, onCompleted])

  // Final progress flush on unmount
  React.useEffect(() => {
    return () => {
      if (!video || !programId) return
      const pct = Math.round(maxPctRef.current)
      if (pct > 0) {
        updateWatchProgress(programId, video.id, {
          watchPct: pct,
          completed: pct >= 90,
        }).catch(() => {})
      }
    }
  }, [video, programId])

  function togglePlay() {
    const el = videoRef.current
    if (!el) return
    if (el.paused) el.play().catch(() => {})
    else el.pause()
  }
  function seekBy(sec) {
    const el = videoRef.current
    if (!el) return
    el.currentTime = Math.max(0, Math.min(el.duration || 0, el.currentTime + sec))
  }
  function changeRate(rate) {
    const el = videoRef.current
    if (!el) return
    el.playbackRate = rate
    setPlaybackRate(rate)
  }
  function toggleFullscreen() {
    const el = videoRef.current?.parentElement
    if (!el) return
    if (document.fullscreenElement) document.exitFullscreen().catch(() => {})
    else el.requestFullscreen?.().catch(() => {})
  }
  async function handleAddBookmark() {
    if (!video || !programId) return
    const note = window.prompt(lang === "id" ? "Catatan bookmark (opsional):" : "Bookmark note (optional):")
    if (note === null) return
    setSavingBookmark(true)
    try {
      const ts = Math.floor(videoRef.current?.currentTime ?? 0)
      const created = await addBookmark(programId, video.id, { timestampSec: ts, note: note || undefined })
      setBookmarks((prev) => [...prev, created].sort((a, b) => a.timestamp_sec - b.timestamp_sec))
      toast.success(lang === "id" ? "Bookmark ditambahkan" : "Bookmark added")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setSavingBookmark(false)
    }
  }
  async function handleDeleteBookmark(id) {
    if (!programId) return
    try {
      await deleteBookmark(programId, id)
      setBookmarks((prev) => prev.filter((b) => b.id !== id))
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    }
  }
  function jumpTo(sec) {
    const el = videoRef.current
    if (!el) return
    el.currentTime = sec
    el.play().catch(() => {})
  }

  const watchPctNow = duration > 0 ? Math.round((currentTime / duration) * 100) : 0
  const watermarkText = `${user?.fullName || user?.email || "—"} · ${new Date().toLocaleString(lang === "id" ? "id-ID" : "en-US")}${currentProgram ? ` · ${currentProgram.code?.toUpperCase()}` : ""}`

  return (
    <div className="col gap-4">
      <div style={{ background: "#000", aspectRatio: "16/9", position: "relative", overflow: "hidden", borderRadius: 14 }}>
        <video
          ref={videoRef}
          style={{ width: "100%", height: "100%", display: "block", background: "#000" }}
          onPlay={() => setPlaying(true)}
          onPause={() => setPlaying(false)}
          onTimeUpdate={(e) => setCurrentTime(e.target.currentTime || 0)}
          onLoadedMetadata={(e) => setDuration(e.target.duration || 0)}
          onEnded={() => {
            setPlaying(false)
            maxPctRef.current = 100
            if (programId && video) {
              updateWatchProgress(programId, video.id, { watchPct: 100, completed: true }).catch(() => {})
              if (!completed) { setCompleted(true); onCompleted?.() }
            }
          }}
          onClick={togglePlay}
          playsInline
          controls={false}
        />

        {/* Watermark overlay */}
        <div
          style={{
            position: "absolute", bottom: 80, right: 16,
            color: "rgba(255,255,255,.55)", fontFamily: "var(--font-mono, monospace)",
            fontSize: 11, letterSpacing: ".04em", textShadow: "0 1px 2px rgba(0,0,0,.8)",
            pointerEvents: "none", animation: "wm-drift 12s linear infinite alternate",
          }}
        >
          {watermarkText}
        </div>
        <style>{`@keyframes wm-drift { from { transform: translateY(0); } to { transform: translateY(-8px); } }`}</style>

        {/* Play overlay when paused */}
        {!playing && (
          <div
            style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "rgba(0,0,0,.25)", cursor: "pointer" }}
            onClick={togglePlay}
          >
            <div style={{ width: 72, height: 72, background: "rgba(255,255,255,0.95)", color: "var(--program-color-deep)", display: "grid", placeItems: "center", borderRadius: "50%", boxShadow: "0 8px 32px rgba(0,0,0,0.3)" }}>
              <Icon name="play" size={28} color="var(--program-color-deep)" />
            </div>
          </div>
        )}

        {/* Control bar */}
        <div style={{ position: "absolute", bottom: 0, left: 0, right: 0, padding: 12, background: "linear-gradient(transparent, rgba(0,0,0,.85))" }}>
          <div
            style={{ height: 5, background: "rgba(255,255,255,.25)", position: "relative", cursor: "pointer", marginBottom: 10 }}
            onClick={(e) => {
              const el = videoRef.current
              if (!el || !el.duration) return
              const rect = e.currentTarget.getBoundingClientRect()
              const pct = (e.clientX - rect.left) / rect.width
              el.currentTime = Math.max(0, Math.min(el.duration, pct * el.duration))
            }}
          >
            <div style={{ position: "absolute", inset: 0, width: `${watchPctNow}%`, background: "var(--program-color)" }} />
            {bookmarks.map((b) => {
              const pct = duration > 0 ? Math.min(100, (b.timestamp_sec / duration) * 100) : 0
              return (
                <div key={b.id} title={b.note || formatTime(b.timestamp_sec)}
                  style={{ position: "absolute", left: `${pct}%`, top: -3, width: 3, height: 11, background: "#FBC02D" }} />
              )
            })}
          </div>

          <div className="row gap-3" style={{ color: "white", fontSize: 13, alignItems: "center" }}>
            <button onClick={togglePlay} style={{ background: "transparent", border: 0, color: "white", cursor: "pointer" }}>
              <Icon name={playing ? "pause" : "play"} size={20} color="white" />
            </button>
            <button onClick={() => seekBy(-10)} className="mono tiny" style={{ background: "transparent", border: "1px solid rgba(255,255,255,.25)", color: "white", padding: "2px 6px", cursor: "pointer" }}>⏪10</button>
            <button onClick={() => seekBy(10)} className="mono tiny" style={{ background: "transparent", border: "1px solid rgba(255,255,255,.25)", color: "white", padding: "2px 6px", cursor: "pointer" }}>10⏩</button>
            <span className="mono" style={{ color: "rgba(255,255,255,.85)" }}>{formatTime(currentTime)} / {formatTime(duration)}</span>
            <div style={{ flex: 1 }} />
            <select
              value={playbackRate}
              onChange={(e) => changeRate(Number(e.target.value))}
              style={{ background: "transparent", color: "white", border: "1px solid rgba(255,255,255,.25)", padding: "2px 4px", fontSize: 12 }}
            >
              {[0.5, 0.75, 1, 1.25, 1.5, 2].map((r) => (
                <option key={r} value={r} style={{ background: "#000" }}>{r}×</option>
              ))}
            </select>
            <button
              onClick={() => {
                const el = videoRef.current
                if (!el) return
                if (document.pictureInPictureElement) document.exitPictureInPicture().catch(() => {})
                else el.requestPictureInPicture?.().catch(() => {})
              }}
              title="Picture-in-Picture"
              style={{ background: "transparent", border: 0, color: "white", cursor: "pointer" }}
            >
              <Icon name="pip" size={16} color="white" />
            </button>
            <button onClick={toggleFullscreen} style={{ background: "transparent", border: 0, color: "white", cursor: "pointer" }}>
              <Icon name="fullscreen" size={16} color="white" />
            </button>
          </div>
        </div>

        {/* Top-left lesson info */}
        {video?.title && (
          <div style={{ position: "absolute", top: 12, left: 16 }}>
            <div className="tag" style={{ background: "rgba(0,0,0,.6)", color: "white", border: "none" }}>
              <Icon name="play" size={11} />{video.title}
            </div>
          </div>
        )}
      </div>

      {/* Bookmarks */}
      <Card
        title={lang === "id" ? "Bookmark" : "Bookmarks"}
        eyebrow={lang === "id" ? "Catatan bertanda waktu" : "Timestamped notes"}
        action={
          <Btn variant="ghost" size="sm" icon={<Icon name="plus" size={12} />} onClick={handleAddBookmark} disabled={savingBookmark}
            title={lang === "id" ? "Tambah bookmark di posisi saat ini" : "Add bookmark at current time"}>
            {lang === "id" ? "Tambah" : "Add"}
          </Btn>
        }
      >
        {bookmarks.length === 0 ? (
          <div className="tiny muted">
            {lang === "id" ? "Belum ada bookmark. Klik 'Tambah' untuk menandai momen penting." : "No bookmarks yet. Click 'Add' to mark important moments."}
          </div>
        ) : (
          <div className="col gap-2">
            {bookmarks.map((b) => (
              <div key={b.id} className="row" style={{ padding: "8px 10px", borderRadius: 6, background: "var(--surface-2)", alignItems: "center" }}>
                <button onClick={() => jumpTo(b.timestamp_sec)} className="mono"
                  style={{ background: "var(--program-color-soft)", color: "var(--program-color-deep)", padding: "2px 6px", borderRadius: 4, fontSize: 11, border: "none", cursor: "pointer" }}>
                  {formatTime(b.timestamp_sec)}
                </button>
                <span style={{ flex: 1, fontSize: 13, marginLeft: 10 }}>{b.note || (lang === "id" ? "(tanpa catatan)" : "(no note)")}</span>
                <Btn variant="ghost" size="sm" icon={<Icon name="close" size={12} />} onClick={() => handleDeleteBookmark(b.id)}
                  style={{ padding: 4, minWidth: 0 }} title={lang === "id" ? "Hapus" : "Delete"} />
              </div>
            ))}
          </div>
        )}
      </Card>
    </div>
  )
}

// =============================================================
// COURSE DETAIL — two-pane viewer (sidebar + overview / lesson)
// =============================================================
function CourseDetailScreen() {
  const { lang, navigate, currentProgramId, currentProgram, pageState, user } = useApp()
  const orgName = useOrgName()
  const isMobile = useIsMobile()
  // Papan bacaan butuh ambangnya sendiri. Tablet 800–1024px lolos ambang 768,
  // jadi daftar materi selebar 300px tetap terpasang dan menyisakan ~440px
  // untuk dokumen — sebab keluhan "terlalu kecil di tablet". isMobile dibiarkan
  // 768 supaya perilaku di tempat lain tidak ikut berubah.
  const isNarrowForReading = useIsMobile(1024)
  const [course, setCourse] = React.useState(null)
  const [loading, setLoading] = React.useState(true)
  const [selectedModId, setSelectedModId] = React.useState(null)
  const [selectedLessonId, setSelectedLessonId] = React.useState(null)
  const [expanded, setExpanded] = React.useState({})

  const loadCourse = React.useCallback(async () => {
    if (!currentProgramId) return
    setLoading(true)
    try {
      let courseId = pageState?.courseId
      if (!courseId) {
        const list = await listCourses(currentProgramId)
        if (list.length === 0) { setCourse(null); return }
        courseId = list[0].id
      }
      const data = await getCourse(currentProgramId, courseId)
      setCourse(data)
      // default-select the first module that has an incomplete lesson, else first
      const firstActive = data.modules?.find((m) => m.lessons.some((l) => !data.progressMap?.[l.id])) || data.modules?.[0]
      if (firstActive) {
        setSelectedModId((prev) => prev || firstActive.id)
        setExpanded((prev) => (Object.keys(prev).length ? prev : Object.fromEntries((data.modules || []).map((m) => [m.id, true]))))
      }
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
      setCourse(null)
    } finally {
      setLoading(false)
    }
  }, [currentProgramId, pageState?.courseId])

  React.useEffect(() => { loadCourse() }, [loadCourse])

  // Datang dari tautan "Dokumen referensi (n)" di Beranda: gulir ke kartunya.
  React.useEffect(() => {
    if (loading || !course || pageState?.fokus !== "dokumen-referensi") return
    document.getElementById("dokumen-referensi")?.scrollIntoView({ behavior: "smooth", block: "start" })
  }, [loading, course, pageState?.fokus])

  if (loading) {
    return <div className="main-inner"><p className="muted">{lang === "id" ? "Memuat kursus…" : "Loading course…"}</p></div>
  }
  if (!course) {
    return (
      <div className="main-inner">
        <h1 className="h1 mb-4">{currentProgram?.name || "—"}</h1>
        <Card>
          <div className="empty" style={{ padding: 60 }}>
            <Icon name="book" size={40} color="var(--text-4)" />
            <div className="muted mt-3">{lang === "id" ? "Belum ada kursus untuk program ini" : "No courses available for this program"}</div>
          </div>
        </Card>
      </div>
    )
  }

  const modules = course.modules || []
  const progressMap = course.progressMap || {}
  const lessonDone = (l) => !!progressMap[l.id]
  const selectedMod = modules.find((m) => m.id === selectedModId) || modules[0]
  const selectedLesson = selectedMod?.lessons.find((l) => l.id === selectedLessonId) || null

  const completedModules = modules.filter((m) => m.lessons.length > 0 && m.lessons.every(lessonDone)).length

  async function handleMarkComplete(lessonId, currentlyCompleted) {
    try {
      await setLessonProgress(currentProgramId, lessonId, !currentlyCompleted)
      await loadCourse()
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message)
    }
  }

  function goToLesson(mod, lesson) {
    // GAME lessons are a full-viewport WebGL embed, so they route to their
    // own standalone page instead of becoming the selected lesson here.
    if (lesson.type === "GAME") {
      // courseId ikut dibawa supaya tombol Kembali mendarat di kursus yang
      // sama, bukan di kursus pertama program.
      navigate("game", { lessonId: lesson.id, lessonTitle: lesson.title, courseId: course.id })
      return
    }
    setSelectedModId(mod.id)
    setSelectedLessonId(lesson.id)
  }
  // Maju/mundur menyusuri SELURUH kursus, bukan hanya modul yang sedang dibuka.
  //
  // Dulu fungsi ini cuma melihat selectedMod.lessons, sehingga di pelajaran
  // terakhir sebuah modul tujuannya selalu null — dan null berarti "kembali ke
  // ringkasan modul". Pada kursus yang tiap modulnya berisi SATU pelajaran —
  // persis bentuk OPRC Level 1, enam modul masing-masing satu PDF — pelajaran
  // pertama sekaligus pelajaran terakhir, jadi tombol "Pelajaran berikutnya"
  // TIDAK PERNAH bisa maju: setiap klik memantul balik ke ringkasan, yang bagi
  // peserta terlihat seperti dilempar mundur ke modul sebelumnya.
  function lessonNav(dir) {
    if (!selectedMod || !selectedLesson) return
    const urutan = modules.flatMap((m) => m.lessons.map((l) => ({ mod: m, lesson: l })))
    const idx = urutan.findIndex((x) => x.lesson.id === selectedLesson.id)
    if (idx === -1) return
    const tujuan = urutan[idx + dir] || null

    if (dir < 0 && !tujuan) return // sudah di pelajaran pertama kursus
    if (dir > 0 && !tujuan) { setSelectedLessonId(null); return } // pelajaran terakhir: kembali ke ringkasan

    // Lewat tombol Sebelumnya/Berikutnya pun pelajaran simulasi harus dibuka
    // sebagai halamannya sendiri; LessonFullPage tidak punya tampilan untuknya
    // dan hanya akan menyisakan panel kosong.
    if (tujuan.lesson.type === "GAME") {
      navigate("game", { lessonId: tujuan.lesson.id, lessonTitle: tujuan.lesson.title, courseId: course.id })
      return
    }
    setSelectedModId(tujuan.mod.id)
    setSelectedLessonId(tujuan.lesson.id)
  }

  return (
    <div className="main-inner" style={{ maxWidth: "none" }}>
      <div style={{ display: "grid", gridTemplateColumns: isNarrowForReading ? "1fr" : "300px 1fr", gap: 20, alignItems: "flex-start" }}>

        {/* ── Sidebar ── */}
        <Card noPad>
          <div className="card-header">
            <div style={{ flex: 1, minWidth: 0 }}>
              <div className="eyebrow">{(currentProgram?.code || orgName).toUpperCase()}</div>
              <div className="h3 mt-1">{lang === "id" ? "Daftar Materi" : "Course outline"}</div>
            </div>
          </div>
          <div style={{ padding: "6px 8px 10px" }}>
            <div style={{ padding: "0 8px 10px", fontSize: 12, color: "var(--text-3)" }}>
              <Progress value={completedModules} max={modules.length || 1} style={{ marginBottom: 5 }} />
              {completedModules} {lang === "id" ? "dari" : "of"} {modules.length} {lang === "id" ? "modul selesai" : "modules complete"}
            </div>

            {modules.map((m, mi) => {
              const isModActive = selectedMod?.id === m.id
              const open = expanded[m.id]
              const done = m.lessons.filter(lessonDone).length
              const total = m.lessons.length
              const pct = total > 0 ? (done / total) * 100 : 0
              const isCompleted = total > 0 && done === total
              return (
                <div key={m.id} style={{ marginBottom: 2 }}>
                  <div style={{ display: "flex", alignItems: "center" }}>
                    <button
                      onClick={() => { setSelectedModId(m.id); setSelectedLessonId(null) }}
                      style={{
                        flex: 1, minWidth: 0, textAlign: "left", padding: "9px 10px", borderRadius: 8, border: "none",
                        background: isModActive && !selectedLesson ? "var(--program-color-soft)" : "transparent",
                        cursor: "pointer", display: "flex", gap: 9, alignItems: "center",
                      }}
                    >
                      <span style={{
                        width: 22, height: 22, borderRadius: "50%", flexShrink: 0,
                        background: isCompleted ? "var(--success)" : isModActive ? "var(--program-color)" : "var(--surface-2)",
                        display: "grid", placeItems: "center",
                        color: isCompleted || isModActive ? "white" : "var(--text-3)", fontSize: 10, fontWeight: 700,
                      }}>
                        {isCompleted ? <Icon name="check" size={11} color="white" /> : mi + 1}
                      </span>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div title={withLabel(modLabel(m, mi), m.title)} style={{ fontSize: 12, fontWeight: isModActive ? 600 : 500, color: isModActive && !selectedLesson ? "var(--program-color-text)" : "var(--text)", lineHeight: 1.3, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                          {withLabel(modLabel(m, mi), m.title)}
                        </div>
                        <div style={{ height: 3, background: "var(--border)", borderRadius: 99, overflow: "hidden", marginTop: 4 }}>
                          <div style={{ width: `${pct}%`, height: "100%", background: isCompleted ? "var(--success)" : "var(--program-color)", borderRadius: 99 }} />
                        </div>
                      </div>
                    </button>
                    <button onClick={() => setExpanded((e) => ({ ...e, [m.id]: !e[m.id] }))}
                      style={{ width: 24, height: 24, borderRadius: 6, border: "none", background: "transparent", color: "var(--text-3)", cursor: "pointer", display: "grid", placeItems: "center", marginRight: 4, flexShrink: 0 }}>
                      <Icon name="chevron_down" size={14} style={{ transform: open ? "rotate(0deg)" : "rotate(-90deg)", transition: "transform 0.2s" }} />
                    </button>
                  </div>

                  {open && (
                    <div style={{ paddingLeft: 14, paddingBottom: 4 }}>
                      {m.lessons.map((l) => {
                        const cfg = LESSON_TYPES[l.type] || LESSON_TYPES.VIDEO
                        const isActive = selectedLesson?.id === l.id
                        const done = lessonDone(l)
                        return (
                          <button key={l.id} onClick={() => goToLesson(m, l)}
                            style={{
                              width: "100%", textAlign: "left", padding: "7px 10px", borderRadius: 6, border: "none",
                              background: isActive ? cfg.soft : "transparent", cursor: "pointer",
                              display: "flex", gap: 8, alignItems: "center", marginBottom: 1,
                            }}>
                            <span style={{
                              width: 16, height: 16, borderRadius: "50%", flexShrink: 0,
                              background: done ? "var(--success)" : "var(--surface-3)", display: "grid", placeItems: "center",
                            }}>
                              {done && <Icon name="check" size={8} color="white" />}
                            </span>
                            <span style={{ color: cfg.color, flexShrink: 0, display: "inline-flex" }}><Icon name={cfg.icon} size={12} color={cfg.color} /></span>
                            <span style={{ flex: 1, fontSize: 11.5, fontWeight: isActive ? 500 : 400, color: isActive ? cfg.color : "var(--text-2)", lineHeight: 1.3 }}>{l.title}</span>
                          </button>
                        )
                      })}
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </Card>

        {/* ── Right pane ── */}
        {selectedLesson ? (
          <LessonFullPage
            course={course}
            mod={selectedMod}
            lesson={selectedLesson}
            programId={currentProgramId}
            currentProgram={currentProgram}
            user={user}
            lang={lang}
            done={lessonDone(selectedLesson)}
            onBack={() => setSelectedLessonId(null)}
            onPrev={() => lessonNav(-1)}
            onNext={() => lessonNav(1)}
            onMarkComplete={() => handleMarkComplete(selectedLesson.id, lessonDone(selectedLesson))}
            onVideoCompleted={loadCourse}
          />
        ) : selectedMod ? (
          <ModuleOverview
            course={course}
            mod={selectedMod}
            modIndex={modules.findIndex((m) => m.id === selectedMod?.id)}
            currentProgram={currentProgram}
            programId={currentProgramId}
            lang={lang}
            lessonDone={lessonDone}
            onOpenLesson={(l) => goToLesson(selectedMod, l)}
          />
        ) : (
          <CourseWithoutModules course={course} currentProgram={currentProgram} programId={currentProgramId} lang={lang} />
        )}
      </div>
    </div>
  )
}

// ── Dokumen referensi kursus ──────────────────────────────────
//
// Berlaku untuk SELURUH kursus, jadi kartunya tidak boleh bergantung pada ada
// tidaknya modul: dulu ia digambar di dalam ikhtisar modul, dan kursus yang belum
// punya modul tidak menampilkan dokumennya sama sekali — padahal sudah terunggah.
// Hanya tampil bila ada isinya.
function ReferenceDocsCard({ course, programId, lang }) {
  const [refBusy, setRefBusy] = React.useState(null)
  const refDocs = (course?.ref_docs || []).map((d) => ({ key: d.id, id: d.id, name: d.name, type: d.file_type }))
  if (refDocs.length === 0) return null

  async function openRefDoc(d) {
    setRefBusy(d.key)
    try {
      const { url } = await getCourseDocDownloadUrl(programId, d.id)
      window.open(url, "_blank", "noopener,noreferrer")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setRefBusy(null)
    }
  }

  return (
    // id: sasaran gulir dari Beranda ("Dokumen referensi (n)").
    <div id="dokumen-referensi">
      <Card title={lang === "id" ? "Dokumen Referensi" : "Reference Documents"} eyebrow={lang === "id" ? "Berlaku untuk seluruh kursus ini" : "Applies to the whole course"} noPad>
        {refDocs.map((d) => (
          <div key={d.key} className="row" style={{ padding: "12px 16px", gap: 12, alignItems: "center", borderBottom: "1px solid var(--border-subtle)" }}>
            <div style={{ width: 32, height: 32, borderRadius: 8, background: "#E0E7FF", color: "#3730A3", display: "grid", placeItems: "center", flexShrink: 0 }}>
              <Icon name="doc" size={15} color="#3730A3" />
            </div>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{d.name}</div>
              {d.type && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{d.type}</div>}
            </div>
            <Btn variant="secondary" size="sm" icon={<Icon name="download" size={13} />} onClick={() => openRefDoc(d)} disabled={refBusy === d.key}>
              {refBusy === d.key ? "…" : (lang === "id" ? "Buka" : "Open")}
            </Btn>
          </div>
        ))}
      </Card>
    </div>
  )
}

// Panel kanan untuk kursus yang belum punya modul: tidak ada ikhtisar modul untuk
// digambar, tetapi dokumen referensi kursus tetap harus terjangkau.
function CourseWithoutModules({ course, currentProgram, programId, lang }) {
  const orgName = useOrgName()
  return (
    <div className="col gap-4">
      <div>
        <div className="eyebrow">{(currentProgram?.code || orgName).toUpperCase()}</div>
        <h1 className="h1" style={{ marginTop: 6 }}>{course.title}</h1>
        <div className="muted mt-2" style={{ fontSize: 14, maxWidth: 640, lineHeight: 1.6 }}>
          {lang === "id"
            ? "Materi pelajaran untuk kursus ini belum tersedia."
            : "Lessons for this course are not available yet."}
        </div>
      </div>
      <ReferenceDocsCard course={course} programId={programId} lang={lang} />
    </div>
  )
}

// ── Module overview pane ──────────────────────────────────────
function ModuleOverview({ course, mod, modIndex, currentProgram, programId, lang, lessonDone, onOpenLesson }) {
  const isMobile = useIsMobile()
  const [dlBusy, setDlBusy] = React.useState(null)
  const [pdfReader, setPdfReader] = React.useState(null)
  if (!mod) return null
  const docs = mod.documents || []

  async function openDoc(d) {
    if (d.file_type === "PDF") {
      setPdfReader({ docId: d.id, docName: d.name })
      return
    }
    setDlBusy(d.id)
    try {
      const { url } = await getModuleDocDownloadUrl(programId, d.id)
      window.open(url, "_blank", "noopener,noreferrer")
    } catch (e) {
      toast.error(e?.response?.data?.error || e.message)
    } finally {
      setDlBusy(null)
    }
  }
  const total = mod.lessons.length
  const done = mod.lessons.filter(lessonDone).length
  const videoCount = mod.lessons.filter((l) => l.type === "VIDEO").length
  const isCompleted = total > 0 && done === total
  const next = mod.lessons.find((l) => !lessonDone(l)) || mod.lessons[0]
  const cfg = next ? (LESSON_TYPES[next.type] || LESSON_TYPES.VIDEO) : null

  return (
    <div className="col gap-4">
      <div>
        <div className="eyebrow row gap-2" style={{ display: "inline-flex", alignItems: "center" }}>
          {modHeading(mod, modIndex, lang)}
          {isCompleted ? <Badge tone="success" dot>{lang === "id" ? "Selesai" : "Completed"}</Badge>
            : done > 0 ? <Badge tone="brand" dot>{lang === "id" ? "Berjalan" : "In progress"}</Badge> : null}
        </div>
        <h1 className="h1" style={{ marginTop: 6 }}>{mod.title}</h1>
        <div className="muted mt-2" style={{ fontSize: 14, maxWidth: 640, lineHeight: 1.6 }}>
          {total} {lang === "id" ? "pelajaran" : "lessons"}
          {isCompleted
            ? (lang === "id" ? " · semua pelajaran selesai — bebas ditinjau kembali." : " · all lessons completed — revisit any time.")
            : (lang === "id" ? " · lanjutkan dari pelajaran berikut." : " · pick up where you left off below.")}
        </div>
      </div>

      {/* Continue-learning hero */}
      {next && (
        <button
          onClick={() => onOpenLesson(next)}
          style={{
            display: "flex", alignItems: "stretch", gap: 0, padding: 0, border: "none", cursor: "pointer", textAlign: "left",
            background: "linear-gradient(135deg, var(--program-color-ink), var(--program-color-deep))",
            borderRadius: 16, overflow: "hidden", color: "white",
            boxShadow: "0 8px 28px -10px rgba(var(--program-color-rgb), 0.45)",
          }}
        >
          <div style={{ width: 200, position: "relative", flexShrink: 0, background: "#0B1417", display: "grid", placeItems: "center" }}>
            <div style={{ width: 52, height: 52, borderRadius: "50%", background: "rgba(255,255,255,0.96)", color: "var(--program-color-deep)", display: "grid", placeItems: "center" }}>
              <Icon name={cfg.icon} size={22} color="var(--program-color-deep)" />
            </div>
          </div>
          <div style={{ padding: "22px 26px", flex: 1, display: "flex", flexDirection: "column", justifyContent: "center" }}>
            <div style={{ fontSize: 10, textTransform: "uppercase", letterSpacing: "0.1em", opacity: 0.75 }}>
              {lessonDone(next) ? (lang === "id" ? "Tinjau pelajaran" : "Review lesson") : (lang === "id" ? "Lanjutkan belajar" : "Continue learning")} · {lessonTypeLabel(next.type, lang)}
            </div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.02em", marginTop: 6, lineHeight: 1.2 }}>{next.title}</div>
          </div>
          <div style={{ padding: 26, display: "flex", alignItems: "center", flexShrink: 0 }}>
            <div style={{ background: "white", color: "var(--program-color-deep)", padding: "10px 18px", borderRadius: 10, fontWeight: 600, fontSize: 13, display: "flex", alignItems: "center", gap: 6 }}>
              {lessonDone(next) ? (lang === "id" ? "Tinjau" : "Review") : (lang === "id" ? "Mulai" : "Start")} <Icon name="arrow_right" size={14} />
            </div>
          </div>
        </button>
      )}

      {/* Stats */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, 1fr)", gap: 12 }}>
        {[
          { label: lang === "id" ? "Pelajaran" : "Lessons", value: `${done}/${total}`, sub: lang === "id" ? "selesai" : "completed" },
          { label: "Video", value: videoCount, sub: lang === "id" ? "di modul ini" : "in this module" },
          { label: lang === "id" ? "Simulasi" : "Simulations", value: mod.lessons.filter((l) => l.type === "GAME").length, sub: lang === "id" ? "di modul ini" : "in this module" },
        ].map((s) => (
          <div key={s.label} style={{ padding: "14px 16px", background: "var(--surface)", border: "1px solid var(--border-subtle)", borderRadius: 10 }}>
            <div className="eyebrow">{s.label}</div>
            <div style={{ fontSize: 22, fontWeight: 600, letterSpacing: "-0.5px", marginTop: 4, lineHeight: 1 }}>{s.value}</div>
            <div className="muted" style={{ fontSize: 11, marginTop: 3 }}>{s.sub}</div>
          </div>
        ))}
      </div>

      {/* Lesson list */}
      <Card title={lang === "id" ? "Pelajaran" : "Lessons"} eyebrow={`${total} ${lang === "id" ? "item" : "items"}`} noPad>
        {mod.lessons.map((l, i) => {
          const cfg = LESSON_TYPES[l.type] || LESSON_TYPES.VIDEO
          const done = lessonDone(l)
          return (
            <div key={l.id} className="row" style={{ padding: "12px 16px", gap: 12, borderBottom: i < mod.lessons.length - 1 ? "1px solid var(--border-subtle)" : "none", cursor: "pointer" }} onClick={() => onOpenLesson(l)}>
              <div style={{ width: 32, height: 32, borderRadius: 8, background: cfg.soft, color: cfg.color, display: "grid", placeItems: "center", flexShrink: 0 }}>
                <Icon name={cfg.icon} size={15} color={cfg.color} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 500, fontSize: 13 }}>{l.title}</div>
                <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{lessonTypeLabel(l.type, lang)}</div>
              </div>
              {done ? <Badge tone="success" dot>{lang === "id" ? "Selesai" : "Done"}</Badge> : <Icon name="chevron_right" size={14} color="var(--text-3)" />}
            </div>
          )
        })}
      </Card>

      {/* Reference documents */}
      <ReferenceDocsCard course={course} programId={programId} lang={lang} />

      {pdfReader && (
        <React.Suspense fallback={null}>
          <PDFReaderModal
            docId={pdfReader.docId}
            docName={pdfReader.docName}
            programId={programId}
            lang={lang}
            onClose={() => setPdfReader(null)}
          />
        </React.Suspense>
      )}

      {/* Module documents */}
      <Card title={lang === "id" ? "Dokumen Modul" : "Module documents"} eyebrow={`${docs.length} ${lang === "id" ? "dokumen" : "documents"}`} noPad={docs.length > 0}>
        {docs.length === 0 ? (
          <div className="empty">
            <Icon name="doc" size={22} color="var(--text-4)" />
            <div style={{ marginTop: 8, fontSize: 13 }} className="muted">
              {lang === "id" ? "Trainer belum mengunggah dokumen untuk modul ini." : "Your trainer hasn't uploaded documents for this module yet."}
            </div>
          </div>
        ) : (
          docs.map((d, i) => {
            const isPDF = d.file_type === "PDF"
            return (
              <div key={d.id} className="row" style={{ padding: "12px 16px", gap: 12, alignItems: "center", borderBottom: i < docs.length - 1 ? "1px solid var(--border-subtle)" : "none" }}>
                <div style={{ width: 32, height: 32, borderRadius: 8, background: tintBg("#B53A2C"), color: tintFg("#B53A2C"), display: "grid", placeItems: "center", flexShrink: 0 }}>
                  <Icon name="doc" size={15} color={tintFg("#B53A2C")} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontWeight: 500, fontSize: 13 }}>{d.name}</div>
                  {d.file_type && <div className="muted" style={{ fontSize: 11, marginTop: 2 }}>{d.file_type}</div>}
                </div>
                <Btn
                  variant="secondary"
                  size="sm"
                  icon={<Icon name={isPDF ? "eye" : "download"} size={13} />}
                  onClick={() => openDoc(d)}
                  disabled={dlBusy === d.id}
                >
                  {dlBusy === d.id ? "…" : isPDF ? (lang === "id" ? "Baca" : "Read") : (lang === "id" ? "Unduh" : "Download")}
                </Btn>
              </div>
            )
          })
        )}
      </Card>
    </div>
  )
}

// ── Full-page lesson viewer ───────────────────────────────────
function LessonFullPage({ course, mod, lesson, programId, currentProgram, user, lang, done, onBack, onPrev, onNext, onMarkComplete, onVideoCompleted }) {
  const cfg = LESSON_TYPES[lesson.type] || LESSON_TYPES.VIDEO
  const modIndex = (course.modules || []).findIndex((m) => m.id === mod.id)
  const orgName = useOrgName()

  return (
    <div>
      {/* Breadcrumb + nav */}
      <div className="row between" style={{ marginBottom: 20, flexWrap: "wrap", gap: 8 }}>
        <div className="row gap-2" style={{ alignItems: "center", flexWrap: "wrap" }}>
          <button className="btn btn-ghost btn-sm" onClick={onBack}>← {lang === "id" ? "Kembali ke modul" : "Back to module"}</button>
          <span className="muted">/</span>
          <span className="muted" style={{ fontSize: 12 }}>{withLabel(modHeading(mod, modIndex, lang), mod.title)}</span>
        </div>
        <div className="row gap-2">
          <Btn size="sm" onClick={onPrev}>← {lang === "id" ? "Sebelumnya" : "Previous"}</Btn>
          <Btn size="sm" variant="primary" onClick={onNext}>{lang === "id" ? "Berikutnya" : "Next"} →</Btn>
        </div>
      </div>

      {/* Header */}
      <div className="row gap-3" style={{ marginBottom: 20, alignItems: "flex-start" }}>
        <div style={{ width: 48, height: 48, borderRadius: 12, flexShrink: 0, background: cfg.soft, color: cfg.color, display: "grid", placeItems: "center" }}>
          <Icon name={cfg.icon} size={20} color={cfg.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div className="row gap-2" style={{ alignItems: "center" }}>
            <div className="eyebrow">{withLabel((currentProgram?.code || orgName).toUpperCase(), modLabel(mod, modIndex))}</div>
            <span style={{ padding: "2px 8px", borderRadius: 4, background: cfg.soft, color: cfg.color, fontSize: 11, fontWeight: 600 }}>{lessonTypeLabel(lesson.type, lang)}</span>
            {done && <Badge tone="success" dot>{lang === "id" ? "Selesai" : "Completed"}</Badge>}
          </div>
          <h1 className="h1" style={{ marginTop: 4 }}>{lesson.title}</h1>
        </div>
      </div>

      {/* Body */}
      {lesson.type === "VIDEO" && (
        lesson.video?.id
          ? <LessonVideoPlayer video={lesson.video} lessonId={lesson.id} programId={programId} user={user} currentProgram={currentProgram} lang={lang} onCompleted={onVideoCompleted} />
          : <Card><div className="empty"><Icon name="video" size={24} color="var(--text-4)" /><div className="muted mt-2">{lang === "id" ? "Video belum tersedia untuk pelajaran ini." : "No video available for this lesson yet."}</div></div></Card>
      )}
      {/* Dokumen dibaca di dalam aplikasi, bukan lewat penampil bawaan
          peramban. Yang dulu ada di sini — tombol "Unduh", <object> yang
          memunculkan toolbar Chrome beserta tombol cetak & simpannya, dan
          tautan cadangan "Buka dokumen" — semuanya jalan unduh, dan semuanya
          hilang bersama penggantian ini. */}
      {lesson.type === "PDF" && (
        lesson.content_url
          ? (
            <React.Suspense fallback={
              <Card><div className="empty" style={{ padding: 40 }}><span className="muted">{lang === "id" ? "Memuat dokumen…" : "Loading document…"}</span></div></Card>
            }>
              <PDFContinuousReader
                programId={programId}
                lessonId={lesson.id}
                title={lesson.title}
                lang={lang}
                done={done}
                onMarkComplete={onMarkComplete}
              />
            </React.Suspense>
          )
          : <Card><div className="empty"><Icon name="doc" size={24} color="var(--text-4)" /><div className="muted mt-2">{lang === "id" ? "Dokumen belum tersedia." : "No document available yet."}</div></div></Card>
      )}
      {/* Bottom nav */}
      <div className="row between" style={{ marginTop: 28 }}>
        <Btn onClick={onPrev}>← {lang === "id" ? "Pelajaran sebelumnya" : "Previous lesson"}</Btn>
        <div className="row gap-2">
          {done
            ? <Btn variant="primary" onClick={onNext}>{lang === "id" ? "Pelajaran berikutnya" : "Next lesson"} <Icon name="arrow_right" size={14} /></Btn>
            : <Btn variant="primary" onClick={() => { onMarkComplete(); onNext() }}>{lang === "id" ? "Tandai selesai & lanjut" : "Mark complete & continue"} <Icon name="arrow_right" size={14} /></Btn>
          }
        </div>
      </div>
    </div>
  )
}

// =============================================================
// STANDALONE VIDEO ROUTE (page === "video")
// Thin wrapper: loads the video by id, reuses LessonVideoPlayer.
// =============================================================
function VideoPlayerScreen() {
  const { lang, navigate, user, currentProgramId, currentProgram, pageState } = useApp()
  const isMobile = useIsMobile()
  const [video, setVideo] = React.useState(null)
  const [loadError, setLoadError] = React.useState(null)

  const lessonId = pageState?.lessonId
  const videoId = pageState?.videoId
  const lessonTitle = pageState?.lessonTitle || ""

  React.useEffect(() => {
    let cancelled = false
    async function load() {
      try {
        if (!videoId) { setLoadError(lang === "id" ? "Video tidak ditentukan." : "No video specified."); return }
        const v = await getVideo(videoId)
        if (!cancelled) setVideo(v)
      } catch (e) {
        if (!cancelled) setLoadError(e?.response?.data?.error || e.message)
      }
    }
    load()
    return () => { cancelled = true }
  }, [videoId, lang])

  if (loadError) {
    return (
      <div className="main-inner">
        <Card>
          <h2 className="h2">{lang === "id" ? "Video tidak dapat dimuat" : "Could not load video"}</h2>
          <p className="muted small mt-2">{loadError}</p>
          <Btn variant="secondary" className="mt-3" onClick={() => navigate("course")}>{lang === "id" ? "Kembali ke kursus" : "Back to course"}</Btn>
        </Card>
      </div>
    )
  }
  if (!video) {
    return <div className="main-inner"><Card tight><p className="muted">{lang === "id" ? "Memuat video…" : "Loading video…"}</p></Card></div>
  }

  return (
    <div className="main-inner" style={{ maxWidth: "none" }}>
      <div className="row between" style={{ marginBottom: 16 }}>
        <div className="row gap-2" style={{ alignItems: "center" }}>
          <Btn variant="ghost" size="sm" icon={<Icon name="chevron_left" size={14} />} onClick={() => navigate("course")}>{lang === "id" ? "Kembali" : "Back"}</Btn>
          {currentProgram && <span className="muted" style={{ fontSize: 12 }}>{currentProgram.code?.toUpperCase()} · {video.title || lessonTitle}</span>}
        </div>
      </div>
      <div className="grid" style={{ gridTemplateColumns: isMobile ? "1fr" : "1fr 320px", gap: 16, alignItems: "flex-start" }}>
        <LessonVideoPlayer video={video} lessonId={lessonId} programId={currentProgramId} user={user} currentProgram={currentProgram} lang={lang} />
        <div className="card" style={{ borderLeft: "3px solid var(--program-color)", overflow: "hidden" }}>
          <div className="row gap-2" style={{ padding: "12px 16px", background: "var(--program-color-soft)" }}>
            <Icon name="ai" color="var(--program-color)" />
            <strong className="small">{lang === "id" ? "Tanya AI tentang video" : "Ask AI about this video"}</strong>
          </div>
          <div style={{ padding: "12px 16px" }}>
            <p className="small muted" style={{ margin: 0 }}>
              {lang === "id" ? "Buka asisten AI program ini untuk pertanyaan terkait materi." : "Open the program AI assistant for material-related questions."}
            </p>
            <Btn variant="primary" size="sm" block className="mt-3" icon={<Icon name="ai" size={12} />} onClick={() => navigate("ai")}>
              {lang === "id" ? "Buka asisten" : "Open assistant"}
            </Btn>
          </div>
        </div>
      </div>
    </div>
  )
}

export { CourseDetailScreen, VideoPlayerScreen }
